const mqListsMap = new Map();
const mqListToKeyMap = new WeakMap(); // Reverse lookup for better performance
const classNamePrefix = 'media-';

export function set(obj) {
  if (!obj || typeof obj !== 'object') {
    console.warn('MediaQueries.set(): Invalid object provided');
    return;
  }

  Object.keys(obj).forEach(key => {
    // Remove existing listener if key already exists
    if (mqListsMap.has(key)) {
      unset(key);
    }

    try {
      const mqList = window.matchMedia(obj[key]);
      mqList.addEventListener('change', mqListListener);
      mqListsMap.set(key, mqList);
      mqListToKeyMap.set(mqList, key); // Store reverse lookup

      // Initialize current state
      mqListListener({ target: mqList });
    } catch (error) {
      console.error(`MediaQueries.set(): Invalid media query for key "${key}":`, obj[key], error);
    }
  });
}

export function unset(key) {
  if (key) {
    const mqList = mqListsMap.get(key);
    if (mqList) {
      mqList.removeEventListener('change', mqListListener);
      mqListToKeyMap.delete(mqList); // Clean up reverse lookup
      mqListsMap.delete(key);

      // Remove corresponding CSS class
      const keyHandle = sanitizeKey(key);
      const className = `${classNamePrefix}${keyHandle}`;
      document.body.classList.remove(className);
    }
  } else {
    // Clear all media queries
    mqListsMap.forEach((mqList, key) => {
      mqList.removeEventListener('change', mqListListener);
      mqListToKeyMap.delete(mqList);

      // Remove corresponding CSS class
      const keyHandle = sanitizeKey(key);
      const className = `${classNamePrefix}${keyHandle}`;
      document.body.classList.remove(className);
    });
    mqListsMap.clear();
  }
}

export function get(key) {
  if (mqListsMap.has(key)) {
    return mqListsMap.get(key);
  }
  return null; // Explicit return for consistency
}

export function getAll() {
  return new Map(mqListsMap); // Return copy to prevent external mutation
}

export function matches(key) {
  const mqList = mqListsMap.get(key);
  return mqList ? mqList.matches : null;
}

export function getAllMatches() {
  const result = {};
  mqListsMap.forEach((mqList, key) => {
    result[key] = mqList.matches;
  });
  return result;
}

// Utility function to sanitize keys for CSS class names
function sanitizeKey(key) {
  return key.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/gi, '');
}

function mqListListener(e) {
  const mqList = e.target;
  const mapKey = mqListToKeyMap.get(mqList); // O(1) lookup instead of O(n) loop

  if (mapKey) {
    const keyHandle = sanitizeKey(mapKey);
    const className = `${classNamePrefix}${keyHandle}`;

    if (mqList.matches) {
      document.body.classList.add(className);
    } else {
      document.body.classList.remove(className);
    }

    // Dispatch custom event with useful data
    document.dispatchEvent(new CustomEvent('mediaquery:change', {
      detail: {
        key: mapKey,
        matches: mqList.matches,
        media: mqList.media,
        className: className
      }
    }));
  } else {
    // Clean up orphaned listener
    mqList.removeEventListener('change', mqListListener);
  }
}
